import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';

import {
  ButtonIconPrimaryStyles,
  ButtonIconPrimaryStylesProps,
} from '../ButtonIconPrimaryStyles/ButtonIconPrimaryStyles';
import {
  ButtonIconPrimarySize,
  ButtonIconPrimarySizeProps,
} from '../ButtonIconPrimarySize/ButtonIconPrimarySize';

const Container = styled.button`
  padding: 0;
  border: none;
  background: transparent;
`;

export type ExtendsInterface = Pick<
  ButtonIconPrimaryStylesProps,
  'buttonStyle' | 'backgroundStyle' | 'colorStyle' | 'active' | 'transparent'
> &
  Pick<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'disabled' | 'className'> &
  Pick<ButtonIconPrimarySizeProps, 'size' | 'icon'>;

interface ButtonIconPrimaryGeneralProps extends ExtendsInterface {
  type?:
    | 'primary'
    | 'secondary'
    | 'secondary-filled'
    | 'blue'
    | 'green'
    | 'black'
    | 'gray'
    | 'neutral'
    | 'white'
    | 'linear-blue';
  shape?: 'square' | 'circle';
  htmlType?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
}

export const ButtonIconPrimaryGeneral = (props: ButtonIconPrimaryGeneralProps) => {
  const {
    className,
    type = 'white',
    buttonStyle,
    backgroundStyle,
    shape,
    colorStyle,
    htmlType,
    icon,
    size,
    active = false,
    disabled = false,
    transparent = false,
    onClick,
  } = props;

  const buttonStyleContent = React.useMemo(() => {
    return <ButtonIconPrimarySize size={size} icon={icon} />;
  }, [size, icon]);

  switch (type) {
    case 'primary':
      return (
        <Container
          className={cl('button-icon-primary-general primary', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'bold'}
            shape={shape || 'circle'}
            color="primary"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'secondary':
      return (
        <Container
          className={cl('button-icon-primary-general secondary', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'outline'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'matte'}
            shape={shape || 'circle'}
            color="neutral"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'secondary-filled':
      return (
        <Container
          className={cl('button-icon-primary-general secondary-filled', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'dark'}
            colorStyle={colorStyle || 'matte'}
            shape={shape || 'circle'}
            color="neutral"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'blue':
      return (
        <Container
          className={cl('button-icon-primary-general blue', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'bold'}
            shape={shape || 'circle'}
            color="blue"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'green':
      return (
        <Container
          className={cl('button-icon-primary-general green', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'bold'}
            shape={shape || 'circle'}
            color="green"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'black':
      return (
        <Container
          className={cl('button-icon-primary-general black', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'bold'}
            shape={shape || 'circle'}
            color="black"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'gray':
      return (
        <Container
          className={cl('button-icon-primary-general gray', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'bold'}
            shape={shape || 'circle'}
            color="gray"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'neutral':
      return (
        <Container
          className={cl('button-icon-primary-general neutral', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'gray'}
            colorStyle={colorStyle || 'matte'}
            shape={shape || 'circle'}
            color="neutral"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );

    case 'linear-blue':
      return (
        <Container
          className={cl('button-icon-primary-general linear-blue', className)}
          type={htmlType}
          disabled={disabled}
          onClick={onClick}
        >
          <ButtonIconPrimaryStyles
            buttonStyle={buttonStyle || 'filled'}
            backgroundStyle={backgroundStyle || 'light'}
            colorStyle={colorStyle || 'bold'}
            shape={shape || 'circle'}
            color="linear-blue"
            content={buttonStyleContent}
            transparent={transparent}
            active={active}
            disabled={disabled}
          />
        </Container>
      );
    case 'white':
    default:
  }

  return (
    <Container
      className={cl('button-icon-primary-general white', className)}
      type={htmlType}
      disabled={disabled}
      onClick={onClick}
    >
      <ButtonIconPrimaryStyles
        buttonStyle={buttonStyle || 'filled'}
        backgroundStyle={backgroundStyle || 'light'}
        colorStyle={colorStyle || 'bold'}
        shape={shape || 'circle'}
        color="white"
        content={buttonStyleContent}
        active={active}
        disabled={disabled}
        transparent={transparent}
      />
    </Container>
  );
};
